<template>
  <Dialog
    is-persistent
    :title="dialogTitle"
    :dialog="isOpened"
    :confirmation-button-text="confirmationButtonText"
    @confirmationAction="addHoliday"
  >
    <template #content>
      <v-form
        ref="form"
        v-model="valid"
      >
        <v-row>
          <v-col class="pb-4">
            <DatePicker
              id="holiday-date-input"
              ref="date"
              v-model="holidayItem.date"
              :rules="dateRules"
              label="Date"
              required
            />
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-text-field
              id="holiday-description-input"
              v-model="holidayItem.description"
              class="secondary-background"
              label="Description"
              :rules="descriptionRules"
              required
              hide-details="auto"
              outlined
              dense
            />
          </v-col>
        </v-row>
        <v-row class="pb-4">
          <v-col>
            <v-select
              v-model="holidayItem.location_id"
              :items="locations"
              item-text="title"
              item-value="id"
              label="Location"
              dense
              hide-details="auto"
              outlined
              required
              :rules="locationRules"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-select
              v-model="holidayItem.type"
              :items="typeOptions"
              label="Type"
              dense
              hide-details
              outlined
              required
              :rules="typeRules"
            />
          </v-col>
        </v-row>
      </v-form>
    </template>
  </Dialog>
</template>

<script>
import moment from 'moment';

import { Dialog, DatePicker } from '@/components/shared';
import { DateHelper } from '@/helpers/date.helper';
import apiClient from '@/services/api-client';

const getInitialDate = () => DateHelper.toIso(moment());

const getInitialState = () => ({
  date: getInitialDate(),
  description: '',
  type: 'holiday',
});

export default {
  components: {
    Dialog,
    DatePicker,
  },
  props: {
    isOpened: {
      type: Boolean,
      required: true,
    },
    locations: {
      type: Array,
      required: true,
    },
  },
      data() {
    return {
      dialogTitle: 'Add Holiday',
      confirmationButtonText: 'Add',
      valid: true,
      holidayItem: getInitialState(),
      dateRules: [
        (value) => !!value || 'Date is required'
      ],
      typeRules: [
        (value) => !!value || 'Type is required'
      ],
      descriptionRules: [
        (value) => !!value?.trim() || 'Description is required'
      ],
      locationRules: [
        (value) => !!value || 'Location is required'
      ],
      typeOptions: [
        { text: 'Holiday', value: 'holiday' },
        { text: 'Work day', value: 'work_day' },
      ],
    };
  },
  computed: {
    form() {
      return { ...this.holidayItem };
    },
  },
  async created() {
    document.addEventListener('keydown', this.handleKeypress);
    document.addEventListener('keyup', this.handleKeyup);
  },
  destroyed() {
    document.removeEventListener('keydown', this.handleKeypress);
    document.removeEventListener('keyup', this.handleKeyup);
  },
  methods: {
    async addHoliday(confirmed) {
      if (!confirmed) {
        this.closeDialog();
        return;
      }

      if (!this.$refs.form.validate()) {
        return;
      }

      try {
        await apiClient.holidayApi.createHoliday({
          ...this.holidayItem,
          location_id: this.holidayItem.location_id,
        });
        this.$emit('save');
        this.closeDialog();
      } catch (error) {
        const errorMessage = error.response?.data?.message || 'An error occurred while saving holiday.';
        this.flashError(errorMessage);
      }
    },
    closeDialog() {
      this.$emit('close');
      this.reset();
    },
    reset() {
      Object.assign(this.holidayItem, getInitialState());
      this.$refs.date.date = getInitialDate();
      this.resetValidation();
    },
    validate() {
      return this.$refs.form.validate();
    },
    handleKeypress(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDialog();
      }
    },
    handleKeyup(event) {
      if (event.key === 'Enter' && event.ctrlKey) {
        event.preventDefault();
        this.addHoliday(true);
      }
    },
    resetValidation() {
      this.$refs.form.resetValidation();
    },
  },
};
</script>
